import _groupBy from 'lodash/groupBy';
import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { flowNodeColor, FlowType } from 'fable/flowEditor';
import { hexStringToNumber } from 'fable/flowEditor/components/flowManager/canvas/renderers/common/colors';
import { PhoneExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'fable/flowEditor/components/flowManager/canvas/types/node';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';

import FiltersModal from '../../../containers/modals/filters';
import {
  FlowCategorySelectItem,
  SystemVariableType,
} from '../../../helpers/flowVariableHelpers';
import { MatchIdType } from '../../modals/filters/criteriaFields';
import {
  ConditionOutcome,
  MatchingOperator,
  MatchingStrategy,
} from '../../types';

export type DateObject = {
  date?: string;
  localDate?: string;
  localTimezone?: string;
  timezone?: string;
};

export type DateRangeObject = {
  startDate?: string;
  localStartDate?: string;
  endDate?: string;
  localEndDate?: string;
  timezone?: string;
  localTimezone?: string;
};

export type FiltersNodeRuleMatch = {
  id: string;
  type: string;
  value?: string | DateRange;
};

export type FiltersNodeRuleVariable = {
  id: string;
  type: string;
  value?: string | DateRange;
};

export type DateRange = {
  start_date: string;
  end_date: string;
};

export type FiltersNodeRule = {
  match: FiltersNodeRuleMatch;
  matching_rule: MatchingOperator;
  variable: FiltersNodeRuleVariable;
};

export type FiltersFormRule = {
  match: string | DateObject | DateRangeObject;
  matching_rule: MatchingOperator;
  variable?: string | FlowCategorySelectItem['id'];
  variableType?: MatchIdType;
};

export type Payload = {
  match_node_id?: string | null;
  matching_strategy: MatchingStrategy;
  non_match_node_id?: string | null;
  rules: FiltersNodeRule[];
  description?: string;
};

type FiltersNodeAttributes = GenericNodeAttributes<Payload, PhoneExtraData>;
type FiltersNodeProps = GenericNodeProps<Payload, PhoneExtraData>;

const translatePayload: FiltersNodeAttributes['translatePayload'] = (node) => {
  const { non_match_node_id, match_node_id } = node.payload;

  return [
    {
      id: uuid(),
      type: 'out',
      label: ConditionOutcome.NO_MATCH,
      connection: non_match_node_id || uuid(),
    },
    {
      id: uuid(),
      type: 'out',
      label: ConditionOutcome.MATCH,
      connection: match_node_id || uuid(),
    },
  ];
};

const updateConnection: FiltersNodeAttributes['updateConnection'] = (
  node,
  connection,
  connectionId,
) => {
  const entryName =
    connection.connectionLabel === ConditionOutcome.MATCH
      ? 'match_node_id'
      : 'non_match_node_id';
  const payload: Payload = {
    ...node.payload,
    [entryName]: connectionId,
  };

  return { ...node, payload };
};

const translateScopeVariables: FiltersNodeAttributes['translateScopeVariables'] =
  (node) => {
    const { payload: { rules = [] } = {} } = node;
    const needs = rules.reduce<string[]>((acc, { variable, match }) => {
      if (variable?.type !== SystemVariableType.System && variable.id) {
        acc.push(variable.id);
      }

      const isMatchScopeVariable = match?.type === SystemVariableType.Scope;
      if (match.id && isMatchScopeVariable) {
        acc.push(match.id);
      }

      return acc;
    }, []);

    return {
      needs: [...new Set(needs)],
      grants: [],
    };
  };

const createFiltersNode = (flowType: FlowType): FiltersNodeAttributes => ({
  iconName: 'branch',
  label: 'Filters',
  color: hexStringToNumber(flowNodeColor.purple),
  helpText:
    'Sort conversations based on whether they match your chosen criteria.',
  translateScopeVariables,
  translatePayload,
  updateConnection,
  NodeContent: FiltersNode,
  ActionModal: FiltersModal(flowType),
});

export const FiltersNodePhoneAttributes = createFiltersNode(
  FlowType.FLOW_TYPE_CALL,
);

export const FiltersNodeEmailAttributes = createFiltersNode(
  FlowType.FLOW_TYPE_EMAIL,
);

export const FiltersNodeMessengerAttributes = createFiltersNode(
  FlowType.FLOW_TYPE_MESSENGER,
);

export function FiltersNode({ node }: FiltersNodeProps): JSX.Element {
  const { payload } = node;
  const { rules, description } = payload;

  const isDescriptionSet = !!description;

  return (
    <TextContent>
      {useMemo(
        () => (
          <>
            <TextEllipsis>
              <Text
                weight="semibold"
                text={isDescriptionSet ? description : 'Add a description'}
              />
            </TextEllipsis>
            <TextEllipsis>
              <Text
                weight="semibold"
                text={`${rules.length} ${
                  rules.length === 1 ? 'rule' : 'rules'
                }`}
              />
            </TextEllipsis>
          </>
        ),
        [isDescriptionSet, description, rules.length],
      )}
    </TextContent>
  );
}
